import { useState } from 'react'
import { SEO } from '@/components/SEO'
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Switch } from "@/components/ui/switch"
import { ArrowLeft, Shield, Users, CheckCircle } from "@phosphor-icons/react"
import { User } from "@/App"
import { toast } from "sonner"
import { motion } from 'framer-motion'
import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth"
import { doc, setDoc } from "firebase/firestore"
import { firebaseApp } from '@/lib/firebase'
import { db } from '@/lib/firebase'

interface LoginPageProps {
  onLogin: (user: User) => void
  onBack: () => void
}

export function LoginPage({ onLogin, onBack }: LoginPageProps) {
  const [isContractor, setIsContractor] = useState(false)

  const handleGoogleLogin = async () => {
    try {
      const auth = getAuth(firebaseApp)
      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(auth, provider)
      
      // Persist isContractor to Firestore so onAuthStateChanged picks it up
      if (isContractor) {
        await setDoc(doc(db, 'users', result.user.uid), { isContractor: true }, { merge: true })
      }

      const user: User = {
        id: result.user.uid,
        email: result.user.email || '',
        name: result.user.displayName || 'Google User',
        isContractor,
        zipCode: undefined
      }

      toast.success('Successfully signed in with Google!')
      onLogin(user)
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to sign in with Google.'
      console.error(error)
      toast.error(message)
    }
  }



  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/10 to-primary/5 py-8 relative overflow-hidden">
      <SEO title="Login | SealcoatPaving.com" description="Sign in to your SealcoatPaving.com account to access your dashboard and manage your contractor profile." path="/login" noindex />
      {/* Floating Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-primary/20 rounded-full"
            animate={{
              x: [0, 100, 0],
              y: [0, -100, 0],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: 10 + i * 2,
              repeat: Infinity,
              delay: i * 2,
            }}
            style={{
              left: `${20 + i * 20}%`,
              top: `${30 + i * 15}%`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-md mx-auto px-4">
        <motion.div 
          className="mb-6"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onBack}
            className="text-muted-foreground hover:text-foreground hover:bg-card/50 backdrop-blur-sm"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Home
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card className="backdrop-blur-lg bg-card/80 border-0 shadow-2xl">
            <CardHeader className="text-center space-y-6 pt-8">
              <motion.div 
                className="mx-auto w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <Shield size={32} className="text-white" />
              </motion.div>
              
              <div>
                <CardTitle className="text-3xl font-bold bg-gradient-to-r from-foreground to-primary bg-clip-text text-transparent">
                  Welcome Back
                </CardTitle>
                <CardDescription className="text-lg mt-3">
                  Sign in with Google to find trusted contractors in your area
                </CardDescription>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-8 pb-8">
              {/* Account Type Selection */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <div className="p-6 border border-border/50 rounded-xl bg-gradient-to-r from-secondary/20 to-muted/20 backdrop-blur-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                        {isContractor ? (
                          <Users size={24} className="text-primary" />
                        ) : (
                          <CheckCircle size={24} className="text-primary" />
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-semibold">
                          {isContractor ? "Join as Contractor" : "Find Contractors"}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {isContractor 
                            ? "Join our network to get customer leads" 
                            : "Search for trusted paving professionals"
                          }
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={isContractor}
                      onCheckedChange={setIsContractor}
                      aria-label={isContractor ? 'Switch to homeowner mode' : 'Switch to contractor mode'}
                      className="data-[state=checked]:bg-primary"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Google Sign In Button */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="space-y-4"
              >
                <Button
                  onClick={handleGoogleLogin}
                  className="w-full h-14 text-base font-semibold bg-white hover:bg-gray-50 text-gray-900 border border-gray-300 shadow-lg hover:shadow-xl transition-all duration-300"
                  size="lg"
                >
                  <svg aria-hidden="true" className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>

              </motion.div>

              {/* Benefits Section */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1 }}
                className="space-y-3"
              >
                {[
                  "Access to verified contractors",
                  "Secure and trusted platform", 
                  "Quick response within 24 hours"
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3 text-sm">
                    <CheckCircle size={16} className="text-green-500" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </div>
                ))}
              </motion.div>

              <motion.div 
                className="text-center text-xs text-muted-foreground"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 1.2 }}
              >
                By signing in, you agree to our{' '}
                <a href="/terms" className="text-primary hover:underline transition-colors">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" className="text-primary hover:underline transition-colors">Privacy Policy</a>
              </motion.div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}